import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useLang } from "../contexts/LanguageContext";

const SUPPORTED = ["de", "en", "tr"];
const LANG_KEY = "qrmp_lang";
const GEO_FLAG = "qrmp_geo_redirected";
const BOT_REGEX = /Googlebot|Bingbot|Slurp|DuckDuckBot|Baiduspider|YandexBot|Sogou|facebookexternalhit|ia_archiver|Twitterbot|LinkedInBot|WhatsApp|TelegramBot|Applebot|AhrefsBot|SemrushBot/i;

const COUNTRY_TO_LANG = {
  // German-speaking
  DE: "de", AT: "de", CH: "de", LI: "de", LU: "de",
  // Turkish-speaking
  TR: "tr",
  // English-speaking
  US: "en", GB: "en", CA: "en", AU: "en", NZ: "en", IE: "en", IN: "en", SG: "en",
  ZA: "en", PH: "en", NG: "en", KE: "en", MT: "en",
};

function isBot() {
  if (typeof navigator === "undefined") return false;
  return BOT_REGEX.test(navigator.userAgent || "");
}

function langFromBrowser() {
  if (typeof navigator === "undefined") return null;
  const langs = navigator.languages || [navigator.language || ""];
  for (const l of langs) {
    const code = (l || "").slice(0, 2).toLowerCase();
    if (SUPPORTED.includes(code)) return code;
  }
  return null;
}

async function langFromGeoIp() {
  try {
    const ctrl = new AbortController();
    const t = setTimeout(() => ctrl.abort(), 1500);
    const res = await fetch("https://ipapi.co/json/", { signal: ctrl.signal, cache: "force-cache" });
    clearTimeout(t);
    if (!res.ok) return null;
    const data = await res.json();
    const cc = (data?.country_code || "").toUpperCase();
    return COUNTRY_TO_LANG[cc] || null;
  } catch (e) {
    return null;
  }
}

/**
 * Mounted once at root. Behavior:
 *
 *  1. URL has /de, /en, or /tr prefix:
 *     → Adopt that language. KEEP the URL prefix intact (no stripping).
 *
 *  2. URL is exactly "/" AND user is a first-time visitor AND not a bot:
 *     → Detect language via navigator + ipapi.co and redirect to /{lang}.
 *     → Mark flag so we never redirect again. Bots & repeat visitors stay on "/".
 *
 *  3. URL has no language prefix and user has a saved choice:
 *     → Keep URL as-is. Sync <html lang>. No redirect.
 */
export default function LangRouteSync() {
  const loc = useLocation();
  const nav = useNavigate();
  const { lang, setLang } = useLang();
  const didCheckRef = useRef(false);

  // Sync URL-prefixed language into context
  useEffect(() => {
    const seg = loc.pathname.split("/").filter(Boolean)[0] || "";
    if (SUPPORTED.includes(seg)) {
      if (lang !== seg) {
        setLang(seg);
        try { localStorage.setItem(LANG_KEY, seg); } catch (e) {}
      }
    }
    if (document.documentElement.lang !== lang) {
      document.documentElement.lang = lang;
    }
  }, [loc.pathname, lang, setLang]);

  // Geo-redirect from "/" — runs once per session, first visit only, skip bots
  useEffect(() => {
    if (didCheckRef.current) return;
    didCheckRef.current = true;

    if (loc.pathname !== "/") return;
    if (isBot()) return;

    let alreadyRedirected = false;
    try {
      alreadyRedirected =
        localStorage.getItem(GEO_FLAG) === "1" ||
        sessionStorage.getItem(GEO_FLAG) === "1";
    } catch (e) {}
    if (alreadyRedirected) return;

    (async () => {
      // Don't redirect if user has any explicit preference
      let saved = null;
      try { saved = localStorage.getItem(LANG_KEY); } catch (e) {}
      let target = saved && SUPPORTED.includes(saved) ? saved : langFromBrowser();
      if (!target) target = await langFromGeoIp();
      if (!target) target = "de";

      try { sessionStorage.setItem(GEO_FLAG, "1"); } catch (e) {}
      try { localStorage.setItem(GEO_FLAG, "1"); } catch (e) {}

      // Only redirect when target differs from default landing language.
      // "/" already serves German content as x-default, so DE users stay on "/".
      if (target === "de") return;

      nav(`/${target}${loc.search}${loc.hash}`, { replace: true });
    })();
  }, [loc.pathname, loc.search, loc.hash, nav]);

  return null;
}
